<template>
  <div class="py-8">
    <v-app-bar flat color="transparent">
      <app-bar-nav-icon />
      <v-toolbar-title class="breadcrumbs d-flex align-center">
        <v-icon class="text-light-blue">{{ icons.arrowLeft }}</v-icon>
        <router-link
          class="text-decoration--none"
          :to="{ name: 'discoveryPages' }"
          >Discover Pages</router-link
        >
      </v-toolbar-title>
    </v-app-bar>

    <div class="ma-4">
      <div class="display-2 font-weight-bold my-6 text-capitalize">
        {{ displayName }}
      </div>

      <div class="d-flex">
        <div>
          <upload-photo
            :image-source="form.cover_photo ? form.cover_photo.url : null"
            @change="onChangeImage"
            @error="
              form.$setErrors({ cover_photo: ['The file size is too large'] })
            "
          />
          <div
            class="v-messages theme--light error--text"
            v-if="form.$getError('cover_photo')"
          >
            {{ form.$getError('cover_photo')[0] }}
          </div>
        </div>
      </div>

      <div class="my-6">
        <v-form ref="form" class="discovery-page-form">
          <label class="text-field-label">Discover Menu</label>
          <v-select
            flat
            solo
            required
            class="mt-2"
            v-model="form.discovery_id"
            :items="discoveries"
            item-value="id"
            item-text="discovery_name"
            :error-messages="form.$getError('discovery_id')"
            :loading="form.$busy"
          ></v-select>

          <label class="text-field-label">Title</label>
          <v-text-field
            flat
            solo
            required
            class="mt-2"
            v-model="form.title"
            :error-messages="form.$getError('title')"
            :loading="form.$busy"
          ></v-text-field>

          <label class="text-field-label">Subtitle</label>
          <v-text-field
            flat
            solo
            required
            class="mt-2"
            v-model="form.subtitle"
            :error-messages="form.$getError('subtitle')"
            :loading="form.$busy"
          ></v-text-field>

          <label class="text-field-label">Introduction</label>
          <content-editor class="my-3" v-model="form.introduction" />
          <div
            class="v-text-field__details pl-3"
            v-if="form.$getError('introduction')"
          >
            <div class="v-messages theme--light error--text" role="alert">
              <div class="v-messages__wrapper">
                <div class="v-messages__message">
                  {{ form.$getError('introduction')[0] }}
                </div>
              </div>
            </div>
          </div>

          <div class="mt-6">
            <label class="text-field-label">Upload photo or video</label>
            <v-file-input
              v-model="attachment"
              flat
              solo
              hide-details
              class="mt-2"
              placeholder="Select file"
              accept="image/*,video/*"
              :loading="form.$busy"
              @change="loadAttachment"
              :clearable="false"
            ></v-file-input>
            <div
              class="v-text-field__details pl-3 mt-2"
              v-if="form.$getError('attachment')"
            >
              <div class="v-messages theme--light error--text" role="alert">
                <div class="v-messages__wrapper">
                  <div class="v-messages__message">
                    {{ form.$getError('attachment')[0] }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="form.attachment && form.attachment.mime_type">
            <div class="d-flex mt-2">
              <a class="caption" @click="removeAttachmentDialog = true"
                >Delete Attachment</a
              >
              <v-spacer></v-spacer>
              <a class="caption" @click="showAttachment = !showAttachment"
                >Show / Hide Attachment Preview</a
              >
            </div>

            <div class="mt-2 text-center" v-if="showAttachment">
              <v-card
                elevation="2"
                color="secondary"
                v-if="form.attachment.mime_type.includes('video')"
              >
                <video
                  height="300px"
                  preload="metadata"
                  controls
                  :poster="form.attachment.thumb_url"
                  style="margin-bottom: -6px"
                >
                  <source
                    :src="form.attachment.url"
                    :type="form.attachment.mime_type"
                  />
                </video>
              </v-card>
              <v-card
                elevation="2"
                color="secondary"
                v-else-if="form.attachment.mime_type.includes('image')"
              >
                <v-img
                  :src="form.attachment.url"
                  :lazy-src="form.attachment.url"
                  contain
                  height="300px"
                ></v-img>
              </v-card>
            </div>
          </div>

          <div class="mt-6"></div>
          <label class="text-field-label">Content</label>
          <content-editor class="my-3" v-model="form.content" />
          <div
            class="v-text-field__details pl-3"
            v-if="form.$getError('content')"
          >
            <div class="v-messages theme--light error--text" role="alert">
              <div class="v-messages__wrapper">
                <div class="v-messages__message">
                  {{ form.$getError('content')[0] }}
                </div>
              </div>
            </div>
          </div>

          <div class="mt-6"></div>
          <label class="text-field-label">Is Premium?</label>
          <v-select
            flat
            solo
            required
            class="mt-2"
            v-model="form.is_premium"
            :items="[
              { value: true, label: 'Yes' },
              { value: false, label: 'No' },
            ]"
            item-value="value"
            item-text="label"
            :error-messages="form.$getError('is_premium')"
            :loading="form.$busy"
          ></v-select>

          <label class="text-field-label">Categories</label>
          <v-select
            flat
            solo
            required
            multiple
            class="mt-2"
            v-model="form.tags"
            :items="tags"
            item-value="id"
            item-text="tag_name"
            :error-messages="form.$getError('tags')"
            :loading="form.$busy"
            @change="isEdited = true"
          ></v-select>
          <div class="d-flex mt-4">
            <v-btn
              v-if="form.id"
              color="error"
              class="mr-4 px-6"
              height="40px"
              :loading="form.$busy"
              @click="deleteDialog = true"
              >Delete</v-btn
            >

            <v-spacer></v-spacer>

            <v-btn
              color="primary"
              class="px-6"
              height="40px"
              @click="
                discoveryPage
                  ? updateDiscoveryPageDetails()
                  : addDiscoveryPage()
              "
              :loading="form.$busy"
              >Save</v-btn
            >
          </div>
        </v-form>

        <v-dialog v-model="deleteDialog" max-width="290">
          <v-card>
            <v-card-title class="headline">Confirm Delete</v-card-title>

            <v-card-text>Are you sure you want to delete this?</v-card-text>

            <v-card-actions>
              <v-btn text @click="deleteDialog = false" :loading="form.$busy"
                >Cancel</v-btn
              >

              <v-spacer></v-spacer>

              <v-btn
                color="red"
                text
                @click="removeDiscoveryPage"
                :loading="form.$busy"
                >Yes, Proceed</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-dialog>
      </div>
    </div>

    <v-dialog v-model="removeAttachmentDialog" max-width="290">
      <v-card>
        <v-card-title class="headline">Confirm Delete</v-card-title>

        <v-card-text
          >Are you sure you want to delete the attachment?</v-card-text
        >

        <v-card-actions>
          <v-btn
            text
            @click="removeAttachmentDialog = false"
            :loading="form.$busy"
            >Cancel</v-btn
          >

          <v-spacer></v-spacer>

          <v-btn
            color="red"
            text
            @click="removeAttachment"
            :loading="form.$busy"
            >Yes, Proceed</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-overlay absolute :value="form.$busy" opacity="0.2">
      <v-progress-circular
        indeterminate
        color="primary"
        size="64"
      ></v-progress-circular>
    </v-overlay>

    <v-snackbar v-model="snackbar.show" right :color="snackbar.color">
      {{ snackbar.message }}
      <v-btn text @click="snackbar.show = false">OK</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import { mdiChevronLeft, mdiCheck, mdiAlertCircleOutline } from '@mdi/js'
import { mapState, mapActions, mapMutations } from 'vuex'
import AppBarNavIcon from '@/layouts/shared/AppBarNavIcon'
import UploadPhoto from '@/components/UploadPhoto'
import ContentEditor from '../Settings/PageManager/components/ContentEditor.vue'

import Form from '@/utils/form'

import axios from 'axios'

export default {
  name: 'DiscoveryPageDetails',

  components: {
    AppBarNavIcon,
    ContentEditor,
    UploadPhoto,
  },

  data() {
    return {
      icons: {
        arrowLeft: mdiChevronLeft,
        check: mdiCheck,
        alert: mdiAlertCircleOutline,
      },
      deleteDialog: false,
      removeAttachmentDialog: false,
      form: new Form({
        cover_photo: '',
        discovery_id: '',
        title: '',
        subtitle: '',
        introduction: '',
        content: '',
        is_premium: '',
        tags: [],
        attachment: '',
        s3_attachment: '',
      }),
      attachment: null,
      file: {
        file_name: '',
        file_size: '',
        content_type: '',
      },
      snackbar: {
        show: false,
        message: null,
        color: '',
      },
      showModal: false,
      showAttachment: false,
      isEdited: false,
      isImageChanged: false,
      isUploadChanged: false,
    }
  },

  computed: {
    ...mapState({
      discoveryPage: (state) => state.discoveryPage.discoveryPageDetails,
      authUser: (state) => state.auth.user,
      tags: (state) => state.tag.list,
      discoveries: (state) => state.discovery.list,
    }),
    displayName() {
      return this.form.title
    },
  },

  methods: {
    ...mapActions({
      getDiscoveryPageDetails: 'discoveryPage/getDiscoveryPageDetails',
      createDiscoveryPage: 'discoveryPage/createDiscoveryPage',
      updateDiscoveryPage: 'discoveryPage/updateDiscoveryPage',
      deleteDiscoveryPage: 'discoveryPage/deleteDiscoveryPage',
      deleteDiscoveryPageAttachment:
        'discoveryPage/deleteDiscoveryPageAttachment',
      getTags: 'tag/getTags',
      getDiscoveries: 'discovery/getDiscoveries',
    }),

    ...mapMutations({
      clearDiscoveryPageDetails: 'discoveryPage/clearDiscoveryPageDetails',
    }),

    async getDiscoveryPage() {
      this.form.$busy = true
      await this.getDiscoveryPageDetails(this.$route.params.id)
      this.form = new Form(this.discoveryPage)
      this.attachment = this.form.attachment
      this.form.$busy = false
    },

    loadAttachment() {
      const file = this.attachment
      this.form.attachment = file
      this.isUploadChanged = true
      // console.log(file)
      // const reader = new FileReader()
      // reader.onload = (e) => {
      //   console.log(e.target.result)
      //   const base64Img = e.target.result
      //   const binaryImg = this.convertDataURIToBinary(base64Img)
      //   const blob = new Blob([binaryImg], { type: file.type })

      //   this.form.attachment = blob
      //   this.isUploadChanged = true
      // }

      // reader.onerror = (e) => {
      //   console.log(e)
      // }
      // reader.readAsDataURL(file)
      this.getSignedURL(file)
    },

    async getSignedURL(file) {
      this.form.$busy = true
      this.file.file_name = file.name
      this.file.file_size = file.size
      this.file.content_type = file.type

      await this.$api
        .post('posts/attachment/upload-url', this.file)
        .then(({ data }) => {
          this.form.$busy = false
          this.form.s3_attachment = data.data
        })
        .catch((err) => {
          this.form.$busy = false
          if (err.response.status === 422) {
            this.form.$setError(
              err.response.data.errors,
              'attachment',
              err.response.data.message
            )
          }
        })
    },

    async uploadAttachmentToS3() {
      this.form.$busy = true
      if (this.form.s3_attachment) {
        await axios
          .put(this.form.s3_attachment.signed_url, this.form.attachment, {
            headers: {
              Accept: 'application/json',
              'Content-Type': this.file.content_type,
            },
          })
          .then(() => {
            this.form.attachment = this.form.s3_attachment.key
          })
          .catch((err) => {
            this.form.$busy = false
            this.form.$setError('attachment', err)
          })
      }
    },

    async updateDiscoveryPageDetails() {
      this.form.$busy = true
      this.form.$clearErrors()
      this.cleanEditors()
      if (!this.isEdited) {
        this.form.tags = this.form.tags.map((n) => n.id)
        this.isEdited = true
      }

      if (this.form.attachment && this.isUploadChanged) {
        await this.uploadAttachmentToS3()
      }

      this.updateDiscoveryPage(this.getFormData())
        .then(() => {
          this.form.$busy = false
          this.isImageChanged = false
          this.isUploadChanged = false
          this.showSnackbar(
            'Discovery page details successfully updated!',
            'success'
          )
          this.$router.replace({ name: 'discoveryPages' })
        })
        .catch((err) => {
          this.form.$busy = false
          if (err.response.status === 422) {
            this.form.$setErrors(err.response.data.errors)
          }
        })
    },

    async addDiscoveryPage() {
      this.form.$busy = true
      this.form.$clearErrors()
      this.cleanEditors()

      if (this.form.attachment && this.isUploadChanged) {
        await this.uploadAttachmentToS3()
      }

      this.createDiscoveryPage(this.getFormData())
        .then(() => {
          this.form.$busy = false
          this.isImageChanged = false
          this.isUploadChanged = false
          this.showSnackbar('Discovery page successfully added!', 'success')
          this.$router.replace({ name: 'discoveryPages' })
        })
        .catch((err) => {
          this.form.$busy = false
          if (err.response.status === 422) {
            this.form.$setErrors(err.response.data.errors)
          }
        })
    },

    removeDiscoveryPage() {
      this.form.$busy = true
      this.deleteDiscoveryPage(this.form.id)
        .then(() => {
          this.form.$busy = false
          this.deleteDialog = false
          this.showSnackbar('Discovery page deleted successfully!')
          this.$router.replace({ name: 'discoveryPages' })
        })
        .catch((err) => {
          this.form.$busy = false
          if (err.response.status === 404) {
            this.deleteDialog = false
            this.showSnackbar(
              'The page you are trying to delete is no longer existing.',
              'error'
            )
          }
        })
    },

    async removeAttachment() {
      this.form.$busy = true
      await this.deleteDiscoveryPageAttachment(this.form.id)
        .then(() => {
          this.form.$busy = false
          this.removeAttachmentDialog = false
          this.showAttachment = false
          this.form.attachment = null
          this.attachment = null
          this.showSnackbar('Attachment deleted successfully!')
        })
        .catch((err) => {
          this.form.$busy = false
          if (err.response.status === 404) {
            this.removeAttachmentDialog = false
            this.showSnackbar(
              'The attachment you are trying to delete is no longer existing.',
              'error'
            )
          }
        })
    },

    async fetchTags(page = 1) {
      this.form.$busy = true
      await this.getTags({ page: page, sort: 'tag_name' })
      this.form.$busy = false
    },

    async fetchDiscoveries(page = 1) {
      this.form.$busy = true
      await this.getDiscoveries({ page: page, sort: 'discovery_name' })
      this.form.$busy = false
    },

    onChangeImage(blob) {
      this.isImageChanged = true
      this.form.$clearErrors()
      this.form.cover_photo = blob
    },

    getFormData() {
      let data = new FormData()
      for (let key in this.form.$data()) {
        data.append(key, this.form[key])
      }
      for (let i = 0; i < this.form.tags.length; i++) {
        data.append('tags[]', this.form.tags[i])
      }
      if (this.isImageChanged) {
        data.append('cover_photo', this.form.cover_photo)
      } else {
        data.delete('cover_photo')
      }
      if (!this.isUploadChanged) {
        data.delete('attachment')
      }
      return data
    },

    showSnackbar(message, color) {
      this.snackbar.message = message
      this.snackbar.show = true
      this.snackbar.color = color
    },

    cleanEditors() {
      let fontStr = "<div style='font-family: Outfit, Outfit-Regular;'>"

      if (this.formatText(this.form.introduction) === '') {
        this.form.introduction = ''
      } else if (!this.form.introduction.includes(fontStr)) {
        this.form.introduction = `${fontStr}${this.form.introduction}</div>`
      }

      if (this.formatText(this.form.content) === '') {
        this.form.content = ''
      } else if (!this.form.content.includes(fontStr)) {
        this.form.content = `${fontStr}${this.form.content}</div>`
      }
    },

    formatText(str) {
      return str.replace(/<\/?[^>]+(>|$)/g, '')
    },

    convertDataURIToBinary(dataURI) {
      const BASE64_MARKER = ';base64,'
      const base64Index = dataURI.indexOf(BASE64_MARKER) + BASE64_MARKER.length
      const base64 = dataURI.substring(base64Index)
      const raw = window.atob(base64)
      const rawLength = raw.length
      var array = new Uint8Array(new ArrayBuffer(rawLength))

      for (let i = 0; i < rawLength; i++) {
        array[i] = raw.charCodeAt(i)
      }
      return array
    },
  },

  created() {
    if (this.$router.currentRoute.name === 'discoveryPage.details') {
      this.getDiscoveryPage()
    }
    this.fetchTags()
    this.fetchDiscoveries()
  },

  destroyed() {
    this.clearDiscoveryPageDetails()
  },

  watch: {
    $route() {
      this.getDiscoveryPage()
    },
  },
}
</script>

<style lang="scss" scoped>
.discovery-page-form {
  width: 600px;

  @media (max-width: 420px) {
    width: 100%;
  }
}
</style>
